import { memo, useCallback, useEffect, useState } from 'react';
import { FieldType, IField } from '@baishuyun/types';
import { FieldTypeNameMap } from '../const/ui';
import { toHumanReadable } from '../lib/utils';
import { Collapsible, CollapsibleContent } from './collapsible';
import LineChecker from './line-checker';

const Checker = ({
  disabled,
  onCheckedChange,

  label,
  val,
  streaming,
  readonly,
}: {
  label: string;
  val: string;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
  desc: string;
  readonly?: boolean;
  streaming?: boolean;
}) => {
  return (
    <LineChecker
      streaming={streaming}
      readonly={readonly}
      title={label}
      disabled={disabled}
      defaultChecked={true}
      onCheckedChange={onCheckedChange}
      className="pl-9"
      shortDesc={val}
    />
  );
};

const SubFieldChecker = memo(
  ({
    field,
    readonly,
    onCheckedChange,
    streaming,
  }: {
    onCheckedChange: (
      checked: boolean,
      field: IField,
    ) => void;
    readonly?: boolean;
    streaming?: boolean;
    field: IField;
  }) => {
    const { widget, label } = field;
    if (!widget) {
      return null;
    }

    const { type, value: v } = widget;
    const t = type as FieldType;
    if (!FieldTypeNameMap.hasOwnProperty(t)) {
      return null;
    }

    const value = toHumanReadable(v, t);

    const typeDesc = FieldTypeNameMap[t] || FieldTypeNameMap.unknown;

    const handleCheckChange = useCallback(
      (v: boolean) => {
        onCheckedChange(v, field);
      },
      [field]
    );

    useEffect(() => {
      // 默认勾选上
      handleCheckChange(true);
    }, []);

    return (
      <Checker
        streaming={streaming}
        readonly={readonly}
        desc={typeDesc}
        label={label}
        val={value as string}
        onCheckedChange={handleCheckChange}
      />
    );
  },
  (p1, p2) => {
    const isNameEqual =
      p1.field.widget?.widgetName === p2.field.widget?.widgetName &&
      p1.readonly === p2.readonly &&
      p1.streaming === p2.streaming;
    return isNameEqual;
  }
);

type F = IField;

interface generatedField {
  label: string;
  fieldName: string;
  fieldType: FieldType;
  value: any;
}

type onSubformFieldChangeParam = {
  checked: boolean;
  field: F;
  parentFieldName: string;
  row?: number;
  col?: number;
};

/**
 * 专门渲染子表单下的字段勾选
 */
export const SubformFieldsValueChecker = ({
  subformField,
  onChange,
  streaming,
  readonly,
}: {
  onChange: (p: onSubformFieldChangeParam) => void;
  subformField: IField;
  streaming?: boolean;
  readonly?: boolean;
}) => {
  const subformValue = subformField.widget?.value;
  if (!Array.isArray(subformValue) || subformValue.length === 0) {
    return null;
  }

  const subformRecords = subformValue as generatedField[][];

  // 默认展开
  const [openAll, setOpenAll] = useState(true);

  return (
    <Collapsible open={openAll} onOpenChange={setOpenAll}>
      <CollapsibleContent>
        {subformRecords.map((rowData: generatedField[], row: number) => {
          if (!rowData || !rowData.length) {
            return null;
          }
          return (
            <Collapsible defaultOpen={true} key={row}>
              <CollapsibleContent>
                {rowData.map((fData: generatedField, col: number) => {
                  return (
                    <SubFieldChecker
                      readonly={readonly}
                      streaming={streaming}
                      field={{
                        label: fData.label,
                        widget: {
                          widgetName: fData.fieldName,
                          type: fData.fieldType,
                          value: fData.value,
                        },
                      }}
                      onCheckedChange={(checked: boolean, field: F) => {
                        onChange({
                          checked,
                          field,
                          row,
                          col,
                          parentFieldName: subformField.widget?.widgetName ?? "",
                        });
                      }}
                    />
                  );
                })}
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </CollapsibleContent>
    </Collapsible>
  );
};
